import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Truck, Archive } from "lucide-react";

export default function VehicleChronicle() {
  const [vehicles, setVehicles] = useState([]);
  const [heat, setHeat] = useState({ rows: [], legacy_rows: [] });

  useEffect(() => {
    api.get("/analytics/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/analytics/chronic-heatmap").then((r) => setHeat(r.data || { rows: [], legacy_rows: [] })).catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const total = vehicles.length;
    const critical = vehicles.filter((v) => v.repeat_count >= 2).length;
    return { total, critical };
  }, [vehicles]);

  // Renk yoğunluğu: repeat_count 0..5+ arası
  const heatColor = (n) => {
    if (n >= 4) return "bg-red-600 text-white";
    if (n >= 2) return "bg-red-400 text-white";
    if (n >= 1) return "bg-amber-300 text-slate-900";
    return "bg-slate-100 text-slate-500";
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Kronik Analiz</div>
        <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Araç Arıza Kroniği</h1>
        <p className="mt-2 text-sm text-slate-500">
          Sistem × Plaka bazlı ısı haritası + tarihsel süreklilik için eski kayıt özeti.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Sistemdeki Araç</div>
          <div className="metric-num text-3xl font-bold mt-1 text-[#1E3A8A]">{stats.total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 border-l-4 border-l-red-500">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kritik Araç (2+ Tekrar Arıza)</div>
          <div className="metric-num text-3xl font-bold mt-1 text-red-600">{stats.critical}</div>
        </div>
      </div>

      {/* Yeni Sistem/Parça ısı haritası */}
      <Card className="border-slate-200 shadow-none" data-testid="chronic-heatmap-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" /> Sistem × Plaka Isı Haritası
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {heat.rows.length === 0 && <div className="text-sm text-slate-500">Henüz yeni Sistem/Parça yapısında kayıt yok.</div>}
          {heat.rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider">Plaka</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Sistem</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Toplam</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Tekrar</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Parça Dağılımı</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Son</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {heat.rows.map((r, i) => (
                    <TableRow key={`${r.plate}-${r.system_id}-${i}`} className="hover:bg-slate-50" data-testid={`heatmap-row-${i}`}>
                      <TableCell className="font-semibold flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-blue-50 text-[#1E3A8A] flex items-center justify-center">
                          <Truck className="w-4 h-4" />
                        </div>
                        <span className="font-mono">{r.plate}</span>
                      </TableCell>
                      <TableCell className="text-xs">{r.system_name}</TableCell>
                      <TableCell className="text-right font-mono">{r.count}</TableCell>
                      <TableCell className="text-right">
                        <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md font-mono font-bold text-xs ${heatColor(r.repeat_count)}`}>
                          {r.repeat_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(r.parts || {}).slice(0, 4).map(([name, cnt]) => (
                            <span key={name} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-mono">
                              {name}·{cnt}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.last_visit ? new Date(r.last_visit).toLocaleDateString("tr-TR") : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Eski Kayıt bölümü */}
      {heat.legacy_rows.length > 0 && (
        <Card className="border-slate-200 shadow-none" data-testid="chronic-legacy-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/60">
            <CardTitle className="font-heading text-base flex items-center gap-2 text-slate-700">
              <Archive className="w-4 h-4 text-slate-500" /> Eski Kayıt (Sistem/Parça öncesi)
            </CardTitle>
            <p className="text-[11px] text-slate-500 mt-1">
              Bu kayıtlar Update 20 öncesinde eski `vehicle_section` alanıyla girilmiş. Otomatik tekrar arıza tespitine DAHİL EDİLMEZ (yanlış alarm önlemek için) — ancak tarihsel süreklilik için burada listelenir.
            </p>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider">Plaka</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Eski Bölüm Etiketi</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Sayı</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Son</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {heat.legacy_rows.map((r, i) => (
                    <TableRow key={`legacy-${i}`} className="hover:bg-slate-50" data-testid={`legacy-row-${i}`}>
                      <TableCell className="font-mono">{r.plate}</TableCell>
                      <TableCell className="text-xs italic">{r.legacy_section}</TableCell>
                      <TableCell className="text-right font-mono">{r.count}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.last_visit ? new Date(r.last_visit).toLocaleDateString("tr-TR") : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Klasik plaka sıralaması korunuyor */}
      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg">Araç Sıralaması (tüm iş emirleri)</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">Plaka</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Toplam Atölye Girişi</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Tekrar Arıza</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Toplam Süre (dk)</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Son Ziyaret</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Risk</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicles.map((v) => {
                  const risk = v.repeat_count >= 3 ? "Yüksek" : v.repeat_count >= 1 ? "Orta" : "Düşük";
                  return (
                    <TableRow key={v.plate} className="hover:bg-slate-50">
                      <TableCell className="font-semibold flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-blue-50 text-[#1E3A8A] flex items-center justify-center">
                          <Truck className="w-4 h-4" />
                        </div>
                        {v.plate}
                      </TableCell>
                      <TableCell className="text-right font-mono">{v.job_count}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{v.repeat_count}</TableCell>
                      <TableCell className="text-right font-mono">{v.total_duration_minutes}</TableCell>
                      <TableCell className="font-mono text-xs">{v.last_visit ? new Date(v.last_visit).toLocaleDateString("tr-TR") : "-"}</TableCell>
                      <TableCell>
                        {risk === "Yüksek" && <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><AlertTriangle className="w-3 h-3 mr-1" />Yüksek</Badge>}
                        {risk === "Orta" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Orta</Badge>}
                        {risk === "Düşük" && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Düşük</Badge>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
